// Generated by Claude Code
import i18n from 'i18next'
import { State } from '#core/state/State.ts'
import ApiUsersApi from '#core/api/auth/ApiUsersApi.ts'
import type ApiUser from '#core/entities/auth/api-users/ApiUser.ts'
import type ApiUserCreated from '#core/entities/auth/api-users/ApiUserCreated.ts'
import { showToast } from '#core/notifications/toast.ts'

const apiUsersApi = new ApiUsersApi()

/** A client id + secret pair shown exactly once, right after it was issued. */
export interface IssuedCredentials {
  kind: 'created' | 'rotated'
  name: string
  identifier: string
  secret: string
}

export interface ApiUserConfirm {
  type: 'rotate' | 'delete'
  id: string
  name: string
}

/** API users tab state; loads lazily so users who never open the tab never hit the endpoint. */
export class ApiUsersState extends State<ApiUsersState> {
  apiUsers: ApiUser[] = []
  loading = false
  loadError = false
  editOpen = false
  editId: string | null = null
  confirm: ApiUserConfirm | null = null
  issued: IssuedCredentials | null = null
  _loaded = false

  constructor(key: string | undefined, storage: Storage | null) {
    super(key, storage)
  }

  get editing(): ApiUser | null {
    return this.apiUsers.find(u => u.id === this.editId) ?? null
  }

  filtered(query: string): ApiUser[] {
    const q = query.toLowerCase().trim()
    if (!q) return this.apiUsers
    return this.apiUsers.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.identifier.toLowerCase().includes(q) ||
      (u.role_name?.toLowerCase().includes(q)),
    )
  }

  async ensureLoaded(): Promise<void> {
    if (this._loaded) return
    this._loaded = true
    await this.reload()
  }

  async reload(): Promise<void> {
    this.reactTo.loading = true
    this.reactTo.loadError = false
    try {
      this.reactTo.apiUsers = await apiUsersApi.listApiUsers()
    } catch {
      this.reactTo.loadError = true
    } finally {
      this.reactTo.loading = false
    }
  }

  openCreate(): void {
    this.reactTo.editId = null
    this.reactTo.editOpen = true
  }

  openEdit(id: string): void {
    this.reactTo.editId = id
    this.reactTo.editOpen = true
  }

  closeEdit(): void {
    this.reactTo.editOpen = false
    this.reactTo.editId = null
  }

  onCreated(created: ApiUserCreated): void {
    this.reactTo.issued = { kind: 'created', name: created.name, identifier: created.identifier, secret: created.secret }
    void this.reload()
  }

  async setEnabled(id: string, enabled: boolean): Promise<void> {
    try {
      const updated = await apiUsersApi.setEnabled(id, enabled)
      if (updated) this.reactTo.apiUsers = this.apiUsers.map(u => (u.id === id ? updated : u))
    } catch {
      showToast(i18n.t('usersTeams.toast.updateUserError', { ns: 'settings' }), 'danger')
    }
  }

  requestConfirm(type: ApiUserConfirm['type'], id: string): void {
    const user = this.apiUsers.find(u => u.id === id)
    this.reactTo.confirm = { type, id, name: user?.name ?? '' }
  }

  cancelConfirm(): void {
    this.reactTo.confirm = null
  }

  /** Runs the confirmed action; rejects on failure so the confirm dialog stays open with its error. */
  async executeConfirm(): Promise<void> {
    const action = this.confirm
    if (!action) return
    if (action.type === 'delete') {
      await apiUsersApi.deleteApiUser(action.id)
      this.reactTo.apiUsers = this.apiUsers.filter(u => u.id !== action.id)
      return
    }
    const issued = await apiUsersApi.rotateSecret(action.id)
    if (!issued) throw new Error('rotate-secret returned no body')
    this.reactTo.issued = { kind: 'rotated', name: action.name, identifier: issued.identifier, secret: issued.secret }
  }

  dismissIssued(): void {
    this.reactTo.issued = null
  }

  copy(value: string): void {
    void navigator.clipboard.writeText(value)
    showToast(i18n.t('usersTeams.apiUsers.copied', { ns: 'settings' }), 'success')
  }
}
